import { GetApiPayloadByUrl } from '@crossbeam/openapi';

import { crossbeamApi } from '@/api';
import {
  HS3_DATA_SOURCE_TYPE,
  HUBSPOT_DATA_SOURCE_TYPE,
  MD_DATA_SOURCE_TYPE,
  PIPEDRIVE_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { captureException } from '@/errors';
import type { SelectOption } from '@/types/filters';

type fieldOptionsType = Record<string, string | number>;

export type SourceDataPayload = GetApiPayloadByUrl<
  '/v0.1/search/source-data',
  'post'
>;

type GetPicklistFieldOptionsArgs = {
  payload: SourceDataPayload;
  fieldOptions?: fieldOptionsType;
  dataSourceType: string;
};

export default async function useGetPicklistFieldOptions({
  payload,
  fieldOptions,
  dataSourceType,
}: GetPicklistFieldOptionsArgs): Promise<SelectOption[]> {
  function getPicklistFieldOptions(
    fieldOptions: fieldOptionsType,
    dataSourceType: string,
  ): SelectOption[] {
    if (
      [
        HUBSPOT_DATA_SOURCE_TYPE,
        SALESFORCE_DATA_SOURCE_TYPE,
        HS3_DATA_SOURCE_TYPE,
      ].includes(dataSourceType)
    ) {
      return Object.entries(fieldOptions).map(([value, label]) => ({
        value,
        label: String(label),
      }));
    } else if (
      [PIPEDRIVE_DATA_SOURCE_TYPE, MD_DATA_SOURCE_TYPE].includes(dataSourceType)
    ) {
      return Object.values(fieldOptions).map((value) => ({
        value: String(value),
        label: String(value),
      }));
    }
    return [];
  }

  const { data, error } = await crossbeamApi.POST('/v0.1/search/source-data', {
    body: payload,
  });

  if (error) {
    captureException(error);
    throw error;
  }

  const responseData = data ?? [];
  const picklistOptions = getPicklistFieldOptions(
    fieldOptions ?? {},
    dataSourceType,
  );
  const labelByValue = new Map(
    picklistOptions.map((option) => [String(option.value), option.label]),
  );

  const responseOptions: SelectOption[] = responseData.map((value) => ({
    value,
    label: labelByValue.get(String(value)) ?? String(value),
  }));

  const seen = new Set<string>();
  return [...responseOptions, ...picklistOptions].filter((option) => {
    const key = String(option.value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
